import * as React from "react";
import {
  RiDownloadLine,
  RiCloseLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "@remixicon/react";
import { cn } from "../../../lib/utils";
import { ButtonSecondary } from "../../atoms/ButtonSecondary";

export interface ImagePreviewModalProps {
  /** Controls whether the modal is open */
  open: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Image source URL */
  src: string;
  /** Alt text for the image */
  alt: string;
  /** Callback when download is clicked */
  onDownload?: () => void;
  /** Callback to open image in new tab */
  onOpenInNewTab?: () => void;
  /** Current image index (1-based) */
  currentIndex?: number;
  /** Total number of images */
  totalImages?: number;
  /** Callback to navigate to previous image */
  onPrevious?: () => void;
  /** Callback to navigate to next image */
  onNext?: () => void;
  /** Image dimensions to display */
  dimensions?: string;
}

/**
 * ImagePreviewModal - Full-screen modal for expanded image preview
 *
 * Features:
 * - Full screen overlay with white background
 * - Large image display centered
 * - Download button (secondary style) + Close button in top-right
 * - Image counter/title above the image
 * - Dimensions below the image
 * - Navigation arrows for multiple images
 *
 * @example
 * ```tsx
 * <ImagePreviewModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   src="https://example.com/image.jpg"
 *   alt="Generated image"
 *   onDownload={handleDownload}
 *   currentIndex={1}
 *   totalImages={3}
 *   dimensions="2048 x 2048"
 * />
 * ```
 */
function ImagePreviewModal({
  open,
  onClose,
  src,
  alt,
  onDownload,
  currentIndex,
  totalImages,
  onPrevious,
  onNext,
  dimensions,
}: ImagePreviewModalProps) {
  // Handle keyboard navigation
  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft" && onPrevious) {
        onPrevious();
      } else if (e.key === "ArrowRight" && onNext) {
        onNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, onPrevious, onNext]);

  // Prevent body scroll when modal is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const showNavigation = onPrevious || onNext;
  const showImageInfo = currentIndex && totalImages;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-white"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
    >
      {/* Top right actions - Download + Close */}
      <div className="absolute right-6 top-4 flex items-center gap-2">
        {onDownload && (
          <ButtonSecondary
            onClick={onDownload}
            leadIcon={<RiDownloadLine size={20} />}
            size="small"
          >
            Download
          </ButtonSecondary>
        )}
        <button
          type="button"
          onClick={onClose}
          className={cn(
            "flex size-12 items-center justify-center rounded-full",
            "text-icon-primary transition-colors hover:bg-bg-hover",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
          )}
          aria-label="Close preview"
        >
          <RiCloseLine className="size-6" />
        </button>
      </div>

      {/* Image title/counter - above image */}
      {showImageInfo && (
        <p className="absolute left-1/2 top-24 -translate-x-1/2 text-center text-base">
          <span className="font-medium text-text-primary">
            Image Option {currentIndex}
          </span>
          <span className="text-text-secondary"> / {totalImages}</span>
        </p>
      )}

      {/* Navigation arrow - Left */}
      {showNavigation && onPrevious && (
        <button
          type="button"
          onClick={onPrevious}
          disabled={currentIndex === 1}
          className={cn(
            "absolute left-6 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full",
            "border border-stroke-secondary bg-white text-icon-primary",
            "transition-colors hover:bg-bg-hover",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
          aria-label="Previous image"
        >
          <RiArrowLeftSLine className="size-6" />
        </button>
      )}

      {/* Navigation arrow - Right */}
      {showNavigation && onNext && (
        <button
          type="button"
          onClick={onNext}
          disabled={currentIndex === totalImages}
          className={cn(
            "absolute right-6 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full",
            "border border-stroke-secondary bg-white text-icon-primary",
            "transition-colors hover:bg-bg-hover",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-stroke-blue",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
          aria-label="Next image"
        >
          <RiArrowRightSLine className="size-6" />
        </button>
      )}

      {/* Centered image container */}
      <div className="flex flex-col items-center gap-4">
        <div className="max-h-[60vh] max-w-[80vw]">
          <img
            src={src}
            alt={alt}
            className="max-h-[60vh] max-w-full object-contain"
          />
        </div>

        {/* Dimensions - below image */}
        {dimensions && (
          <p className="text-sm text-text-primary">{dimensions}</p>
        )}
      </div>
    </div>
  );
}

ImagePreviewModal.displayName = "ImagePreviewModal";

export { ImagePreviewModal };
